import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useListWhatsChanged, useListCategories } from "@workspace/api-client-react";
import {
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Truck,
  KeyRound,
  Search,
  RefreshCw,
  UserCheck,
  Globe,
  HelpCircle,
} from "lucide-react";

function getIcon(name: string) {
  const cls = "w-4 h-4";
  switch (name) {
    case "key-round": return <KeyRound className={cls} />;
    case "truck": return <Truck className={cls} />;
    case "file-text": return <FileText className={cls} />;
    case "user-check": return <UserCheck className={cls} />;
    case "refresh-cw": return <RefreshCw className={cls} />;
    case "search": return <Search className={cls} />;
    case "check-circle-2": return <CheckCircle2 className={cls} />;
    case "globe": return <Globe className={cls} />;
    default: return <HelpCircle className={cls} />;
  }
}

const statusConfig = {
  improved: {
    label: "Improved",
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  changed: {
    label: "Changed",
    className: "bg-blue-50 text-blue-700 border-blue-200",
  },
  watch: {
    label: "Known Issues",
    className: "bg-amber-50 text-amber-700 border-amber-200",
  },
} as const;

export default function WhatsChangedPage() {
  const { data: changes, isLoading } = useListWhatsChanged();
  const { data: categories } = useListCategories();
  const categoryById = new Map((categories ?? []).map((c) => [c.id, c]));

  const improved = changes?.filter((c) => c.status === "improved").length ?? 0;
  const watch = changes?.filter((c) => c.status === "watch").length ?? 0;

  return (
    <Layout>
      <PageHead
        title="What Changed with MOTUS"
        description="A plain-language summary of how the FMCSA's MOTUS registration system changed compared to the legacy tools — including known issues and workarounds."
        path="/whats-changed"
      />
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="text-[var(--dna-indigo-700)] border-[var(--dna-indigo-200)] bg-[var(--dna-indigo-50)] font-medium">
              Launched May 19, 2026
            </Badge>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 leading-tight">
            What Changed with MOTUS
          </h1>
          <p className="text-slate-500 text-base leading-relaxed">
            FMCSA replaced its legacy registration systems with MOTUS on May 19, 2026.
            Here's how key workflows have changed — and what to watch out for during
            the transition.
          </p>

          {/* Quick stats */}
          <div className="flex flex-wrap gap-3 mt-5">
            <div className="flex items-center gap-1.5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {improved} improvements
            </div>
            <div className="flex items-center gap-1.5 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              {watch} areas with active bugs
            </div>
          </div>
        </div>

        {/* Change list */}
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {(changes ?? []).map((change) => {
              const config = statusConfig[change.status as keyof typeof statusConfig] ?? statusConfig.changed;
              return (
                <div
                  key={change.id}
                  className="border border-slate-200 rounded-xl bg-white overflow-hidden"
                >
                  {/* Category header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
                    <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
                      <span className="text-[var(--dna-indigo-500)]">{getIcon(change.icon)}</span>
                      {categoryById.get(change.categoryId)?.name ?? "—"}
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-xs font-medium ${config.className}`}
                    >
                      {config.label}
                    </Badge>
                  </div>

                  {/* Old vs New */}
                  <div className="grid md:grid-cols-[1fr_auto_1fr] gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                    <div className="px-4 py-3">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                        Old Way
                      </p>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {change.oldWay}
                      </p>
                    </div>

                    <div className="hidden md:flex items-center justify-center px-3 text-slate-300">
                      <ArrowRight className="w-4 h-4" />
                    </div>

                    <div className="px-4 py-3">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                        MOTUS
                      </p>
                      <p className="text-sm text-slate-700 leading-relaxed font-medium">
                        {change.newWay}
                      </p>
                    </div>
                  </div>

                  {/* Note strip */}
                  {change.note && (
                    <div className="flex items-start gap-2 px-4 py-2.5 bg-amber-50 border-t border-amber-100">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-700">{change.note}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer note */}
        <div className="mt-8 p-4 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-500 leading-relaxed">
          <strong className="text-slate-700">Note:</strong> This page reflects
          known changes as of May 2026 based on carrier reports and FMCSA
          announcements. Content is updated as more information becomes
          available. If you've found a discrepancy or a bug in MOTUS,{" "}
          <a href="/report" className="dna-link font-medium">
            submit a report
          </a>
          .
        </div>
      </div>
    </Layout>
  );
}
